import React, { useEffect, useState, useRef } from 'react';
// images dark
import homeDark from '../images/dark/home_dark_blue.png';
import newMessageDark from '../images/dark/new_message_dark_blue.png';
import profileDark from '../images/dark/profile_dark_system.png';
import tenantDark from '../images/dark/tenant_dark_green.png';
import todosDark from '../images/dark/todos_dark_red.png';
import viewMessagesDark from '../images/dark/view_message_dark_green.png';

// images light
import home from '../images/light/home_light_green.png';
import newMessage from '../images/light/new_message_light_blue.png';
import profile from '../images/light/profile_light_white.png';
import tenant from '../images/light/tenant_light_blue.png';
import todo from '../images/light/todos_light_white.png';
import viewMessages from '../images/light/view_message_light_system.png';

const IMAGES_DARK = [
  { src: homeDark, offset: 120, title: 'Tableau de bord', description: 'Vue d\'ensemble intuitive de votre espace de travail' },
  { src: todosDark, offset: 140, title: 'Gestion des tâches', description: 'Organisez et suivez vos tâches efficacement' },
  { src: newMessageDark, offset: 160, title: 'Nouveau message', description: 'Communication structurée et ciblée' },
  { src: profileDark, offset: 180, title: 'Profil utilisateur', description: 'Personnalisez votre expérience' },
  { src: tenantDark, offset: 200, title: 'Organisation', description: 'Gérez votre équipe et vos permissions' },
  { src: viewMessagesDark, offset: 220, title: 'Messagerie', description: 'Historique complet et recherche avancée' },
];

const IMAGES_LIGHT = [
  { src: home, offset: 120, title: 'Tableau de bord', description: 'Vue d\'ensemble intuitive de votre espace de travail' },
  { src: todo, offset: 140, title: 'Gestion des tâches', description: 'Organisez et suivez vos tâches efficacement' },
  { src: newMessage, offset: 160, title: 'Nouveau message', description: 'Communication structurée et ciblée' },
  { src: profile, offset: 180, title: 'Profil utilisateur', description: 'Personnalisez votre expérience' },
  { src: tenant, offset: 200, title: 'Organisation', description: 'Gérez votre équipe et vos permissions' },
  { src: viewMessages, offset: 220, title: 'Messagerie', description: 'Historique complet et recherche avancée' },
];

function getInitialIsDark() {
  if (typeof window === 'undefined') return false;
  // prefer stored user choice, then explicit class on document, then system preference
  try {
    const stored = localStorage.getItem('pulsecio_theme');
    if (stored === 'dark') return true;
    if (stored === 'light') return false;
  } catch (e) {}

  if (document.documentElement.classList.contains('dark')) return true;
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

const StackSection = () => {
  const [isDark, setIsDark] = useState(getInitialIsDark());
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const section = sectionRef.current;
      const rect = section.getBoundingClientRect();
      const scrollProgress = Math.max(0, Math.min(1, -rect.top / (rect.height - window.innerHeight)));
      const newIndex = Math.floor(scrollProgress * IMAGES_DARK.length);
      setActiveIndex(Math.min(newIndex, IMAGES_DARK.length - 1));
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleThemeChange = (ev) => {
      if (ev && ev.detail) { setIsDark(ev.detail === 'dark'); return; }

      try {
        const stored = localStorage.getItem('pulsecio_theme');
        if (stored === 'dark') { setIsDark(true); return; }
        if (stored === 'light') { setIsDark(false); return; }
      } catch (e) {}

      if (document.documentElement.classList.contains('dark')) { setIsDark(true); return; }

      const prefersDarkNow = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDark(!!prefersDarkNow);
    };

    window.addEventListener('theme-change', handleThemeChange);
    const onStorage = (e) => { if (!e.key || e.key === 'pulsecio_theme') handleThemeChange(); };
    window.addEventListener('storage', onStorage);

    const mql = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)');
    if (mql) {
      if (mql.addEventListener) mql.addEventListener('change', handleThemeChange);
      else if (mql.addListener) mql.addListener(handleThemeChange);
    }

    return () => {
      window.removeEventListener('theme-change', handleThemeChange);
      window.removeEventListener('storage', onStorage);
      if (mql) {
        if (mql.removeEventListener) mql.removeEventListener('change', handleThemeChange);
        else if (mql.removeListener) mql.removeListener(handleThemeChange);
      }
    };
  }, []);

  const IMAGES = isDark ? IMAGES_DARK : IMAGES_LIGHT;

  return (
    <section id="galerie" className="relative py-8" ref={sectionRef}>
      <div className="text-center mb-16 md:mb-20 animate-fadeInUp">
        <h2 className="min-h-32 md:min-h-20 text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 bg-gradient-to-r from-blue-500 to-blue-600 dark:from-blue-700 dark:to-blue-800 bg-clip-text text-transparent">
          Découvrez PulseCIO en images
        </h2>
        <p className="text-base md:text-lg lg:text-xl text-muted max-w-2xl mx-auto px-4 text-black dark:text-white/70">
          Une interface moderne et intuitive pour une collaboration optimale
        </p>
      </div>
      
      <div className="stack-progress-bar">
        {IMAGES.map((_, i) => (
          <div
            key={i}
            className={`stack-progress-dot ${i === activeIndex ? 'active' : i < activeIndex ? 'passed' : ''}`}
          />
        ))}
      </div>

      {IMAGES.map((img, i) => (
        <div
          key={i}
          className={`relative flex w-full mb-12 md:mb-16 rounded-3xl animate-fadeInUp ${i === activeIndex ? 'active' : ''}`}
          style={{ '--offset': `${img.offset}px`, zIndex: IMAGES.length - i, animationDelay: `${img.offset}ms` }}
        >
          <div className={`flex flex-col lg:flex-row items-center justify-between w-full gap-8 lg:gap-16 p-6 md:p-10 lg:p-12 rounded-3xl border transition-all duration-500 
            ${i === activeIndex ? 'shadow-2xl' : 'shadow-lg'}
            bg-white dark:bg-[#1a1f2e] border-gray-200/50 dark:border-gray-700/50`}>
            <div className="flex-none lg:w-80 flex flex-col gap-4 text-center lg:text-left opacity-70 hover:opacity-100 transition-opacity duration-400">
              <span className="text-5xl md:text-6xl font-extrabold leading-none bg-gradient-to-br from-blue-500 to-blue-700 bg-clip-text text-transparent opacity-70">
                0{i + 1}
              </span>
              <h3 className="text-2xl md:text-3xl font-bold m-0 text-black dark:text-white">
                {img.title}
              </h3>
              <p className="text-base md:text-lg leading-relaxed m-0 text-black dark:text-white/70">
                {img.description}
              </p>
            </div>
            <div className="flex-1 flex justify-center items-center relative">
              <div className="stack-card-glow"></div>
              <img 
                src={img.src} 
                alt={img.title} 
                className="w-11/12 md:w-10/12 h-auto rounded-2xl border border-gray-200/30 dark:border-gray-700/30 
                  shadow-[0_25px_50px_rgba(0,0,0,0.2)] dark:shadow-[0_25px_50px_rgba(0,0,0,0.5)]
                  transform transition-all duration-600 ease-out
                  hover:scale-105 hover:-translate-y-2"
                style={{ 
                  transform: i === activeIndex ? 'scale(1)' : 'scale(0.95)',
                  boxShadow: i === activeIndex 
                    ? '0 30px 70px rgba(0, 0, 0, 0.3)' 
                    : '0 25px 50px rgba(0, 0, 0, 0.2)'
                }}
              />
            </div>
          </div>
        </div>
      ))}
    </section>
  );
};

export default StackSection;
